import React, { useState } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from './ui/alert-dialog';
import { Button } from './ui/button';
import { Loader2, CreditCard, AlertTriangle, RefreshCw, CalendarDays } from 'lucide-react';

/**
 * BacklogFeeDialog - shown when user selects a past start month
 * Displays the per-transaction fee, requires card, and handles failures.
 */
const BacklogFeeDialog = ({
  open,
  onOpenChange,
  feeInfo,
  onConfirm,
  onCancel,
}) => {
  if (!feeInfo) return null;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-lg">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Backlog Processing Fee
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-left">
              <p>
                You've selected <strong>{(() => { try { return new Intl.DateTimeFormat('en', {month:'long',year:'numeric'}).format(new Date(feeInfo.start_date)); } catch { return feeInfo.start_date; }})()}</strong> as your start month, which is before the current month.
              </p>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
                <p className="text-sm font-medium text-amber-800">
                  Backlog fee: <strong>{feeInfo.symbol}{feeInfo.per_transaction_rate}</strong> per transaction
                  {feeInfo.minimum_fee > 0 && (
                    <span className="ml-1 text-xs">
                      (minimum <strong>{feeInfo.symbol}{feeInfo.minimum_fee}</strong> per bank)
                    </span>
                  )}
                </p>
                {feeInfo.free_available > 0 && (
                  <p className="text-xs text-emerald-700 bg-emerald-50 rounded p-2">
                    Your account has a lifetime <strong>free backlog allowance</strong> of{' '}
                    <strong>{feeInfo.free_available}</strong> transaction{feeInfo.free_available === 1 ? '' : 's'} remaining —
                    these come out of the total charge first.
                  </p>
                )}
                <ul className="text-xs text-amber-700 space-y-1">
                  <li>Charged per bank account when connected</li>
                  <li>Tier-aware rate — Growth and Pro plans pay less per transaction than Starter</li>
                  <li>Current month's transactions are free</li>
                  <li>Bank charges (stamp duty, fees, etc.) are excluded from the count</li>
                  <li>AI classification on backlog transactions is included — you are not charged again for AI usage</li>
                </ul>
              </div>
              
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm font-semibold text-red-800">
                  This fee is charged immediately, regardless of any free trial.
                </p>
                <p className="text-xs text-red-700 mt-1">
                  A payment card is required to proceed with this start month.
                </p>
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel} data-testid="backlog-fee-cancel">
            Use Current Month Instead
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            className="bg-[#108981] hover:bg-[#108981]/90"
            data-testid="backlog-fee-confirm"
          >
            <CreditCard className="w-4 h-4 mr-2" />
            I Understand · Proceed
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

/**
 * BacklogChargeFailedDialog - shown when backlog charge fails
 * Gives user 3 options: update card, retry, or change date
 */
export const BacklogChargeFailedDialog = ({
  open,
  onOpenChange,
  chargeError,
  amount,
  currency,
  symbol,
  transactionCount,
  onRetry,
  onUpdateCard,
  onChangeDate,
  retrying = false,
}) => {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-lg text-red-600">
            <AlertTriangle className="w-5 h-5" />
            Payment Failed
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-left">
              <p>
                We couldn't charge <strong>{symbol}{amount?.toLocaleString()}</strong> for <strong>{transactionCount}</strong> backlog transaction{transactionCount > 1 ? 's' : ''} (bank charges excluded).
              </p>
              {chargeError && (
                <p className="text-sm text-red-600 bg-red-50 rounded-lg p-2">
                  {chargeError}
                </p>
              )}
              <p className="text-sm text-muted-foreground">
                Connection discarded. Choose how to continue.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col sm:flex-col gap-2">
          <Button
            onClick={onUpdateCard}
            variant="outline"
            className="w-full justify-start gap-2"
            data-testid="backlog-update-card"
          >
            <CreditCard className="w-4 h-4" />
            Update Card Details
          </Button>
          <Button
            onClick={onRetry}
            disabled={retrying}
            variant="outline"
            className="w-full justify-start gap-2"
            data-testid="backlog-retry-charge"
          >
            {retrying ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            {retrying ? 'Retrying...' : 'Retry Charge'}
          </Button>
          <Button
            onClick={onChangeDate}
            variant="outline"
            className="w-full justify-start gap-2"
            data-testid="backlog-change-date"
          >
            <CalendarDays className="w-4 h-4" />
            Change Start Month
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default BacklogFeeDialog;
